import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  CreateDeliveryInput,
  Delivery,
  UpdateDeliveryInput,
} from "types/Delivery";
import {
  fetchDeliveriesByOrder,
  createDelivery,
  updateDelivery,
  deleteDelivery,
} from "services/deliveries.service";

interface DeliveriesState {
  deliveries: Delivery[];
  selectedDelivery: Delivery | null;
  isLoading: boolean;
  isError: boolean;
  errMsg: string | null;
}

const initialState: DeliveriesState = {
  deliveries: [],
  selectedDelivery: null,
  isLoading: false,
  isError: false,
  errMsg: null,
};

const DeliveriesSlice = createSlice({
  name: "deliveries",
  initialState,
  reducers: {
    setLoading(state) {
      state.isLoading = true;
      state.isError = false;
      state.errMsg = null;
    },
    setDeliveries(state, action: PayloadAction<Delivery[]>) {
      state.isLoading = false;
      // Filter out any invalid deliveries
      state.deliveries = (action.payload || []).filter((d) => d && d.id);
    },
    setSelectedDelivery(state, action: PayloadAction<Delivery | null>) {
      state.selectedDelivery = action.payload;
    },
    addDelivery(state, action: PayloadAction<Delivery>) {
      // Only add if delivery has valid id
      if (action.payload && action.payload.id) {
        state.deliveries.push(action.payload);
      }
    },
    updateDeliveryInList(state, action: PayloadAction<Delivery>) {
      const index = state.deliveries.findIndex(
        (d) => d.id === action.payload.id
      );
      if (index !== -1) {
        state.deliveries[index] = action.payload;
      }
      if (state.selectedDelivery?.id === action.payload.id) {
        state.selectedDelivery = action.payload;
      }
    },
    removeDelivery(state, action: PayloadAction<string>) {
      state.deliveries = state.deliveries.filter(
        (d) => d.id !== action.payload
      );
      if (state.selectedDelivery?.id === action.payload) {
        state.selectedDelivery = null;
      }
    },
    setError(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.isError = true;
      state.errMsg = action.payload;
    },
    clearError(state) {
      state.isError = false;
      state.errMsg = null;
    },
    resetDeliveries(state) {
      state.deliveries = [];
      state.selectedDelivery = null;
      state.isLoading = false;
      state.isError = false;
      state.errMsg = null;
    },
  },
});

export const {
  setLoading,
  setDeliveries,
  setSelectedDelivery,
  addDelivery,
  updateDeliveryInList,
  removeDelivery,
  setError,
  clearError,
  resetDeliveries,
} = DeliveriesSlice.actions;

export default DeliveriesSlice.reducer;

// Thunks
export const fetchOrderDeliveries =
  (companyId: string, orderId: string) => async (dispatch: any) => {
    dispatch(setLoading());
    try {
      const response = await fetchDeliveriesByOrder(companyId, orderId);
      dispatch(setDeliveries(response?.deliveries || []));
    } catch (e: any) {
      dispatch(setError(e.message || "Failed to fetch deliveries"));
    }
  };

export const createOrderDelivery =
  (
    companyId: string,
    orderId: string,
    data: CreateDeliveryInput
  ) =>
  async (dispatch: any) => {
    try {
      const response = await createDelivery(companyId, orderId, data);
      const delivery = response?.delivery;
      if (delivery && delivery.id) {
        dispatch(addDelivery(delivery));
      }
      return delivery;
    } catch (e: any) {
      dispatch(setError(e.message || "Failed to create delivery"));
      throw e;
    }
  };

export const updateOrderDelivery =
  (
    companyId: string,
    deliveryId: string,
    data: UpdateDeliveryInput
  ) =>
  async (dispatch: any) => {
    try {
      const { delivery } = await updateDelivery(companyId, deliveryId, data);
      dispatch(updateDeliveryInList(delivery));
      return delivery;
    } catch (e: any) {
      dispatch(setError(e.message || "Failed to update delivery"));
      throw e;
    }
  };

export const deleteOrderDelivery =
  (companyId: string, deliveryId: string) => async (dispatch: any) => {
    try {
      await deleteDelivery(companyId, deliveryId);
      dispatch(removeDelivery(deliveryId));
    } catch (e: any) {
      dispatch(setError(e.message || "Failed to delete delivery"));
      throw e;
    }
  };
